import {
  CreditLimitLevel,
  CustomerCreditBalance,
  CustomerDocumentBalance,
  DocumentCreditLimit,
} from '../../types/credit';

/** Normalized Kreditlimit view — the document-bound and the customer-only
    endpoint expose the same numbers under different names. */
export interface CreditView {
  /** Backend assessment; only the document-bound endpoint returns one. */
  level: CreditLimitLevel | null;
  /** Kreditrahmen 1 / Warnschwelle — null when no limit is configured. */
  warning: number | null;
  /** Kreditrahmen 2 / hartes Limit — null when no limit is configured. */
  limit: number | null;
  /** Customer balance; excludes `currentDocumentBalance` in the document view. */
  balance: number | null;
  currentDocumentBalance: number | null;
  /** balance + current document — what the backend checked against the limits. */
  total: number | null;
  due: number | null;
  unpaid: number | null;
  documentBalance: readonly { label: string; value: number }[];
}

/** Fields the customer detail carries itself — used while the credit endpoint
    has not answered (or is unavailable on this system). */
export interface CreditFallback {
  accountingBalance?: number | null;
  creditFrame1?: number | null;
  creditFrame2?: number | null;
}

const DOCUMENT_BALANCE_LABELS: Record<keyof CustomerDocumentBalance, string> = {
  blanketOrder: 'Rahmenaufträge',
  blanketOrderDropship: 'Rahmenaufträge Strecke',
  salesOrder: 'Aufträge',
  salesOrderDropship: 'Aufträge Strecke',
  deliveryNote: 'Lieferscheine',
  invoice: 'Rechnungen',
};

// A limit of 0 means "not configured" — it must render as "—", not "0,00".
const configured = (value: number | null | undefined): number | null =>
  !value ? null : value;

/** The document-bound result wins whenever there is one: it is the only source
    of the backend's `level`, and it keeps the current document out of the
    customer balance instead of counting it twice. */
export function toCreditView(
  documentLimit: DocumentCreditLimit | null | undefined,
  customerBalance: CustomerCreditBalance | null | undefined,
  fallback: CreditFallback | null | undefined
): CreditView | null {
  if (documentLimit) {
    return {
      level: documentLimit.level,
      warning: configured(documentLimit.warning),
      limit: configured(documentLimit.limit),
      balance: documentLimit.balance,
      currentDocumentBalance: documentLimit.currentDocumentBalance,
      total: documentLimit.balance + documentLimit.currentDocumentBalance,
      due: null,
      unpaid: null,
      documentBalance: [],
    };
  }

  if (customerBalance) {
    return {
      level: null,
      warning: configured(customerBalance.warningLimit),
      limit: configured(customerBalance.limit),
      balance: customerBalance.balance,
      currentDocumentBalance: null,
      total: null,
      due: customerBalance.due,
      unpaid: customerBalance.unpaid,
      documentBalance: (
        Object.keys(DOCUMENT_BALANCE_LABELS) as (keyof CustomerDocumentBalance)[]
      )
        .map((key) => ({
          label: DOCUMENT_BALANCE_LABELS[key],
          value: customerBalance.documentBalance?.[key] ?? 0,
        }))
        .filter((row) => row.value !== 0),
    };
  }

  if (!fallback) return null;
  return {
    level: null,
    warning: configured(fallback.creditFrame1),
    limit: configured(fallback.creditFrame2),
    balance: fallback.accountingBalance ?? null,
    currentDocumentBalance: null,
    total: null,
    due: null,
    unpaid: null,
    documentBalance: [],
  };
}
